import React, { useState, useCallback } from 'react'
import { StyleSheet, TextInput, View, Alert } from 'react-native'
import { useTheme, useNavigation } from '@react-navigation/native'
import { Button, Icon, Screen, Text, FeatureTooltip } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { useFtStartSocialImport } from '@hooks'
import { translateApiError } from '@utils'
import { NetworkSelector } from './components/NetworkSelector'

type Network = 'instagram' | 'facebook' | 'twitter' | 'linkedin'

export const FtSocialImport = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const navigation = useNavigation()

  const [network, setNetwork] = useState<Network | null>(null)
  const [username, setUsername] = useState('')

  const startImport = useFtStartSocialImport()

  const handleStartImport = useCallback(async () => {
    if (!network || !username.trim()) return

    try {
      await startImport.mutateAsync({
        network,
        username: username.trim(),
      })
      Alert.alert(t('ftSocialImport.launched.title'), t('ftSocialImport.launched.message'))
      navigation.goBack()
    } catch (e: unknown) {
      // Translate the API `{ code, message, params }` envelope; fall back
      // to the English message or a generic key. Reading `e.message`
      // directly leaks "Request failed with status code 422" into the UI.
      Alert.alert(t('ftSocialImport.error.title'), translateApiError(t, e).message)
    }
  }, [network, username, startImport, navigation, t])

  const placeholderText =
    network === 'linkedin'
      ? t('ftSocialImport.placeholder.linkedin')
      : t('ftSocialImport.placeholder.generic')

  return (
    <Screen>
      <View style={styles.header}>
        <Icon name="arrowBack" onPress={() => navigation.goBack()} />
        <Text type="titleBold" color={theme.colors.black}>
          {t('ftSocialImport.title')}
        </Text>
        <View style={{ width: 24 }} />
      </View>

      <FeatureTooltip
        id="social_import"
        title={t('featureTooltip.social_import.title')}
        description={t('featureTooltip.social_import.description')}
        icon="users"
      />

      <View style={styles.content}>
        <Text type="body" color={theme.colors.secondary} style={styles.label}>
          {t('ftSocialImport.chooseNetwork')}
        </Text>
        <NetworkSelector selectedNetwork={network} onSelect={setNetwork} />

        {network && (
          <>
            <Text type="body" color={theme.colors.secondary} style={styles.label}>
              {network === 'linkedin'
                ? t('ftSocialImport.linkedinNameLabel')
                : t('ftSocialImport.usernameLabel')}
            </Text>
            <TextInput
              style={styles.input}
              placeholder={placeholderText}
              placeholderTextColor={theme.colors.lightGrey}
              value={username}
              onChangeText={setUsername}
              autoCapitalize="none"
              autoCorrect={false}
            />

            <Button
              title={t('ftSocialImport.startImport')}
              onPress={handleStartImport}
              disabled={!username.trim() || startImport.isPending}
              containerStyle={styles.button}
            />
          </>
        )}
      </View>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.md,
    },
    content: {
      flex: 1,
      paddingHorizontal: theme.spacing.lg,
      gap: theme.spacing.lg,
    },
    label: {
      marginTop: theme.spacing.md,
    },
    input: {
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: theme.spacing.sm,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      fontSize: 16,
      color: theme.colors.black,
    },
    button: {
      marginTop: theme.spacing.md,
    },
  })
